import { Translation } from 'hooks/useTranslation/useTranslation';
import { FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';

import {
  filterAccessDefault,
  filterOriginDefault,
  filterRoleDefault,
  filterUserExpiresDefault,
  hasEnabledFilter
} from './userFilterConstants';
import { FilterUserExpires, UsersFilterState } from './userFilterTypes';

interface Params {
  translation: Translation;
  filters: UsersFilterState;
}

export const createUserFilterSections = ({ translation, filters }: Params): FilterSection[] => {
  const { filterAccess, filterUserExpires, filterRole, filterOrigin } = filters.filter;
  const { setFilterAccess, setFilterUserExpires, setFilterRole, setFilterOrigin } = filters.setters;

  return [
    {
      id: 'access',
      header: translation.access,
      type: 'checkbox',
      items: [
        {
          key: 'accessAppDisconnected',
          label: translation.access_app_disconnected,
          checked: filterAccess.accessAppDisconnected,
          onChange: () =>
            setFilterAccess((current) => ({
              ...current,
              accessAppDisconnected: !current.accessAppDisconnected
            }))
        },
        {
          key: 'configAppDisconnected',
          label: translation.config_app_disconnected,
          checked: filterAccess.configAppDisconnected,
          onChange: () =>
            setFilterAccess((current) => ({
              ...current,
              configAppDisconnected: !current.configAppDisconnected
            }))
        },
        {
          key: 'noGroup',
          label: translation.no_group,
          checked: filterAccess.noGroup,
          onChange: () =>
            setFilterAccess((current) => ({
              ...current,
              noGroup: !current.noGroup
            }))
        },
        {
          key: 'hasCard',
          label: translation.has_card,
          checked: filterAccess.hasCard,
          onChange: () =>
            setFilterAccess((current) => ({
              ...current,
              hasCard: !current.hasCard
            }))
        },
        {
          key: 'noCard',
          label: translation.no_card,
          checked: filterAccess.noCard,
          onChange: () =>
            setFilterAccess((current) => ({
              ...current,
              noCard: !current.noCard
            }))
        }
      ],
      isClearDisabled: !hasEnabledFilter(filterAccess),
      onClear: () => setFilterAccess(filterAccessDefault)
    },
    {
      id: 'user',
      header: translation.user,
      type: 'radio',
      items: [
        {
          key: FilterUserExpires.Expired,
          label: translation.expired,
          checked: filterUserExpires === FilterUserExpires.Expired,
          onChange: () => setFilterUserExpires(FilterUserExpires.Expired)
        },
        {
          key: FilterUserExpires.OneDay,
          label: translation.expires_in_24h,
          checked: filterUserExpires === FilterUserExpires.OneDay,
          onChange: () => setFilterUserExpires(FilterUserExpires.OneDay)
        },
        {
          key: FilterUserExpires.OneWeek,
          label: translation.expires_this_week,
          checked: filterUserExpires === FilterUserExpires.OneWeek,
          onChange: () => setFilterUserExpires(FilterUserExpires.OneWeek)
        }
      ],
      isClearDisabled: filterUserExpires === null,
      onClear: () => setFilterUserExpires(filterUserExpiresDefault)
    },
    {
      id: 'role',
      header: translation.role,
      type: 'checkbox',
      items: [
        {
          key: 'keyManager',
          label: translation.key_manager,
          checked: filterRole.keyManager,
          onChange: () =>
            setFilterRole((current) => ({
              ...current,
              keyManager: !current.keyManager
            }))
        },
        {
          key: 'administrator',
          label: translation.administrator,
          checked: filterRole.administrator,
          onChange: () =>
            setFilterRole((current) => ({
              ...current,
              administrator: !current.administrator
            }))
        },
        {
          key: 'installer',
          label: translation.installer,
          checked: filterRole.installer,
          onChange: () =>
            setFilterRole((current) => ({
              ...current,
              installer: !current.installer
            }))
        }
      ],
      isClearDisabled: !hasEnabledFilter(filterRole),
      onClear: () => setFilterRole(filterRoleDefault)
    },
    {
      id: 'origin',
      header: translation.origin,
      type: 'checkbox',
      items: [
        {
          key: 'entraId',
          label: translation.entra_id,
          checked: filterOrigin.entraId,
          onChange: () =>
            setFilterOrigin((current) => ({
              ...current,
              entraId: !current.entraId
            }))
        },
        {
          key: 'atlas',
          label: translation.atlas,
          checked: filterOrigin.atlas,
          onChange: () =>
            setFilterOrigin((current) => ({
              ...current,
              atlas: !current.atlas
            }))
        },
        {
          key: 'keySharing',
          label: translation.key_sharing,
          checked: filterOrigin.keySharing,
          onChange: () =>
            setFilterOrigin((current) => ({
              ...current,
              keySharing: !current.keySharing
            }))
        }
      ],
      isClearDisabled: !hasEnabledFilter(filterOrigin),
      onClear: () => setFilterOrigin(filterOriginDefault)
    }
  ];
};
